import Box from '@mui/material/Box';
import { SxProps } from '@mui/system';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Job } from '~hooks/useJob/models';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { Order } from '~hooks/useOrders/models';

import GeofenceReports from './SideBarComponents/GeofenceReports';
import OrderList from './SideBarComponents/OrderList';
import TreadLiveTopBar from './TreadLiveTopBar';
import { LiveMapTabFilter } from './types';

interface TreadLiveSideBarProps {
  onSelectJob: (job: Job) => void;
  onSelectOrder: (order: Order | undefined) => void;
  selectedJob?: Job;
  selectedOrder?: Order;
  sx?: SxProps;
  onViewJobRoute: (job: Job) => void;
  isSidebarCollapsed?: boolean;
  onGeofenceLocationChange?: (coordinates: { lat: number; lng: number } | null) => void;
  onGeofenceDrawingCoordinatesChange?: (
    coordinates: { lat: number; lng: number }[],
    isPolygonMode: boolean,
  ) => void;
  onGeofenceCircleRadiusChange?: (radius: number | null) => void;
  geofenceRecenterPosition?: { lat: number; lng: number } | null;
  geofenceCircleRadius?: number | null;
  geofenceDrawingCoordinates?: { lat: number; lng: number }[];
  isMapPickMode?: boolean;
  onPickLocationModeToggle?: () => void;
  pickedMapCoordinates?: { lat: number; lng: number } | null;
  onPickedMapCoordinatesProcessed?: () => void;
  onGeofenceViewRoute?: (pings: LocationHistoryMinimal[], job?: Job) => void;
  onGeofenceViewRouteLoadingChange?: (isLoading: boolean) => void;
  /** Called when user switches away from Geofence Reports tab (so parent can clear drawn geofence) */
  onGeofenceReportsTabLeave?: () => void;
}

const TreadLiveSideBar = observer(
  ({
    onSelectJob,
    onSelectOrder,
    selectedJob,
    selectedOrder,
    onViewJobRoute,
    isSidebarCollapsed,
    onGeofenceLocationChange,
    onGeofenceDrawingCoordinatesChange,
    onGeofenceCircleRadiusChange,
    geofenceRecenterPosition,
    geofenceCircleRadius,
    geofenceDrawingCoordinates,
    isMapPickMode,
    onPickLocationModeToggle,
    pickedMapCoordinates,
    onPickedMapCoordinatesProcessed,
    onGeofenceViewRoute,
    onGeofenceViewRouteLoadingChange,
    onGeofenceReportsTabLeave,
  }: TreadLiveSideBarProps) => {
    const [activeTab, setActiveTab] = useState<LiveMapTabFilter>(
      LiveMapTabFilter.IN_PROGRESS,
    );

    const handleTabChange = (tab: LiveMapTabFilter) => {
      setActiveTab(tab);
      // Clear geofence route and drawn geofence when navigating away from Geofence Reports tab
      if (tab !== LiveMapTabFilter.GEOFENCE_REPORTS) {
        onGeofenceViewRoute?.([] as LocationHistoryMinimal[]);
        onGeofenceReportsTabLeave?.();
      }
    };

    const renderContent = () => {
      if (isSidebarCollapsed) {
        return null;
      }

      if (activeTab === LiveMapTabFilter.GEOFENCE_REPORTS) {
        return (
          <GeofenceReports
            onLocationChange={onGeofenceLocationChange}
            onDrawingCoordinatesChange={onGeofenceDrawingCoordinatesChange}
            onCircleRadiusChange={onGeofenceCircleRadiusChange}
            geofenceRecenterPosition={geofenceRecenterPosition}
            geofenceCircleRadius={geofenceCircleRadius}
            drawingCoordinates={geofenceDrawingCoordinates}
            isMapPickMode={isMapPickMode}
            onPickLocationModeToggle={onPickLocationModeToggle}
            pickedMapCoordinates={pickedMapCoordinates}
            onPickedMapCoordinatesProcessed={onPickedMapCoordinatesProcessed}
            onViewRoute={onGeofenceViewRoute}
            onViewRouteLoadingChange={onGeofenceViewRouteLoadingChange}
          />
        );
      }

      return (
        <OrderList
          onSelectJob={onSelectJob}
          onSelectOrder={onSelectOrder}
          selectedJob={selectedJob}
          selectedOrder={selectedOrder}
          onViewJobRoute={onViewJobRoute}
          isSidebarCollapsed={isSidebarCollapsed}
        />
      );
    };

    return (
      <Box
        sx={(theme) => ({
          borderRight: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        })}
      >
        <TreadLiveTopBar
          isSidebarCollapsed={isSidebarCollapsed}
          onTabChange={handleTabChange}
        />

        {renderContent()}
      </Box>
    );
  },
);

export default TreadLiveSideBar;
